import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';
import { TriageDeck } from './TriageDeck.js';

type DecisionList = Awaited<ReturnType<typeof api.get<'/api/v1/decisions'>>>;
type Decision = DecisionList['items'][number];
type MailboxList = Awaited<ReturnType<typeof api.get<'/api/v1/mailboxes'>>>;

const CORRECTIONS = [
  'not_now',
  'never_again',
  'wrong_subject',
  'wrong_value',
  'too_risky',
] as const;

/**
 * The decision queue.
 *
 * The screen where a person forms their opinion of whether this product can be trusted with their
 * mail, so three things are deliberate and none of them is decoration.
 *
 * **Saying no asks why.** Not as friction — as the only way the queue gets shorter over time. A no
 * that teaches nothing means the same proposal returns tomorrow, and after two or three rounds the
 * person stops reading, which puts the decisions that matter somewhere nobody looks.
 *
 * **High risk is never in a batch.** The whole point of the label is that such a decision gets
 * looked at on its own. Letting one ride along inside "approve these" makes the label mean nothing.
 *
 * **The preview says when it is a sample.** Five of 1,240 and five of five are different claims.
 */
export function DecisionsScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [queue, setQueue] = useState<DecisionList | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  /* Which mailbox the queue speaks for; null is every mailbox. Tenant-wide proposals — a
     sender rule, say — belong to no mailbox and appear in every scope. */
  const [scopeMailboxId, setScopeMailboxId] = useState<string | null>(null);
  const [mailboxes, setMailboxes] = useState<MailboxList['mailboxes'] | null>(null);
  /* One question at a time, or the whole queue at once. Triage is the fast lane for a long
     queue; the list stays the place where batches and full previews live. */
  const [mode, setMode] = useState<'list' | 'triage'>('list');
  /* The deck's snapshot, taken when triage starts. See TriageDeck on why it is not live. */
  const [deck, setDeck] = useState<readonly Decision[]>([]);

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const result = await api.get(
          '/api/v1/decisions',
          scopeMailboxId === null ? undefined : { mailboxId: scopeMailboxId },
          { tenantId, signal },
        );
        setQueue(result);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setQueue(null);
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
        if (error instanceof TransportError) setFailure('UNREACHABLE');
      }
    },
    [tenantId, scopeMailboxId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /* The filter's options. Fetched once, not per scope change: the set of mailboxes does not
     depend on which of them is selected. */
  useEffect(() => {
    if (tenantId === undefined) return;
    const controller = new AbortController();
    void api
      .get('/api/v1/mailboxes', undefined, { tenantId, signal: controller.signal })
      .then((result) => setMailboxes(result.mailboxes))
      .catch(() => setMailboxes(null));
    return () => controller.abort();
  }, [tenantId]);

  const answer = useCallback(
    async (decision: Decision, correction?: (typeof CORRECTIONS)[number]) => {
      if (tenantId === undefined) return;
      setBusy(decision.id);
      try {
        if (correction === undefined) {
          await api.post(
            '/api/v1/decisions/{decisionId}/approve',
            undefined,
            { decisionId: decision.id },
            { tenantId },
          );
        } else {
          await api.post(
            '/api/v1/decisions/{decisionId}/reject',
            { correction },
            { decisionId: decision.id },
            { tenantId },
          );
        }
        setAsking(null);
        await load();
      } catch (error) {
        // A second answer from another tab is a race a person can actually cause, so it reads as
        // "someone already answered this" rather than as an incident.
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(null);
      }
    },
    [load, tenantId],
  );

  if (failure !== null && queue === null) {
    return (
      <Alert tone="critical" title={t('decision.error.title')}>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (queue === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  const chips =
    mailboxes !== null && mailboxes.length > 1 ? (
      <nav className="mailbox-chips" aria-label={t('today.scope.label')}>
        <button
          type="button"
          className={scopeMailboxId === null ? 'mailbox-chip mailbox-chip--active' : 'mailbox-chip'}
          aria-pressed={scopeMailboxId === null}
          onClick={() => setScopeMailboxId(null)}
        >
          {t('today.scope.all')}
        </button>
        {mailboxes.map((mailbox) => (
          <button
            key={mailbox.id}
            type="button"
            className={
              scopeMailboxId === mailbox.id ? 'mailbox-chip mailbox-chip--active' : 'mailbox-chip'
            }
            aria-pressed={scopeMailboxId === mailbox.id}
            onClick={() => setScopeMailboxId(mailbox.id)}
          >
            {mailbox.emailAddress}
          </button>
        ))}
      </nav>
    ) : null;

  if (queue.items.length === 0) {
    return (
      <>
        {chips}
        <section className="card">
          <h1 className="card__title">{t('decision.queue.title')}</h1>
          <p className="empty__body">{t('decision.queue.empty')}</p>
        </section>
      </>
    );
  }

  if (mode === 'triage') {
    return (
      <>
        <header className="greeting">
          <h1 className="greeting__title">{t('decision.queue.title')}</h1>
          <p className="greeting__summary">
            <Button onClick={() => setMode('list')}>{t('triage.backToList')}</Button>
          </p>
        </header>
        {chips}
        <TriageDeck
          decisions={deck}
          corrections={CORRECTIONS}
          onApprove={(decision) => answer(decision)}
          onReject={(decision, correction) =>
            answer(decision, correction as (typeof CORRECTIONS)[number])
          }
          onExhausted={() => {
            setMode('list');
            void load();
          }}
        />
      </>
    );
  }

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('decision.queue.title')}</h1>
        <p className="greeting__summary">
          {t('decision.queue.counts', {
            high: queue.counts.high,
            medium: queue.counts.medium,
            low: queue.counts.low,
          })}
        </p>
        <p className="greeting__summary">
          <Button
            onClick={() => {
              setDeck(queue.items);
              setMode('triage');
            }}
          >
            {t('triage.start')}
          </Button>
        </p>
      </header>

      {chips}

      {failure !== null && (
        <Alert tone="critical" title={t('decision.error.conflict')}>
          <Button onClick={() => void load()}>{t('today.retry')}</Button>
        </Alert>
      )}

      {queue.items.map((decision) => (
        <section className="card decision" key={decision.id}>
          <div className="decision__head">
            <span className={`badge badge--${decision.risk === 'high' ? 'critical' : 'high'}`}>
              {t(`decision.risk.${decision.risk}`)}
            </span>
            <h2 className="decision__title">
              {translator.t(decision.titleKey, decision.titleParams as Record<string, string>)}
            </h2>
          </div>

          <p className="decision__meta">
            {decision.reversible ? t('decision.reversible') : t('decision.irreversible')}
            {decision.autoApproved && ` · ${t('decision.autoApproved')}`}
          </p>

          {decision.preview !== null && (
            <div className="decision__preview">
              <p className="decision__meta">
                {/* Five of 1,240 and five of five are different claims. */}
                {decision.preview.complete
                  ? t('decision.preview.showingAll', { total: decision.preview.total })
                  : t('decision.preview.showing', {
                      count: decision.preview.examples.length,
                      total: decision.preview.total,
                    })}
              </p>
              <ul className="today-list">
                {decision.preview.examples.map((example) => (
                  <li className="today-list__item" key={example.id}>
                    <span className="today-list__subject">{example.subject}</span>
                    <span className="today-list__meta">{example.from}</span>
                  </li>
                ))}
              </ul>
              {/* The reversal story, next to the button rather than in a support article. */}
              <p className="decision__meta">{translator.t(decision.preview.undoKey)}</p>
            </div>
          )}

          {asking === decision.id ? (
            <div className="decision__reasons">
              <p className="decision__meta">{t('decision.why.title')}</p>
              {CORRECTIONS.map((correction) => (
                <Button
                  key={correction}
                  variant="secondary"
                  disabled={busy === decision.id}
                  onClick={() => void answer(decision, correction)}
                >
                  {t(`decision.why.${correction}`)}
                </Button>
              ))}
            </div>
          ) : (
            <div className="decision__actions">
              <Button disabled={busy === decision.id} onClick={() => void answer(decision)}>
                {t('decision.approve')}
              </Button>
              {/*
                Saying no opens the reasons rather than closing the card. Not friction: it is the
                only way the queue gets shorter over time.
              */}
              <Button
                variant="secondary"
                disabled={busy === decision.id}
                onClick={() => setAsking(decision.id)}
              >
                {t('decision.reject')}
              </Button>
            </div>
          )}
        </section>
      ))}
    </>
  );
}
